import React, { useEffect, useState } from "react";
import Modal from "ui/Modal";
import IssueSearch, { Suggestion } from "components/Issues/IssueSearch";
import { Issue } from "util/constants";
import EntityAvatar from "components/Entities/EntityAvatar";
import { Info, X } from "lucide-react";
import { getEntityDetails } from "util/getObjectDetails";
import { createIssueFromWikipedia } from "util/issueCreationUtils";

export interface TargetIssue {
  id: number;
  name?: string;
  cname?: string;
  wikipedia_link?: string;
}

interface TargetsModalProps {
  isOpen: boolean;
  initialTargets: TargetIssue[];
  onClose: () => void;
  onSave: (targets: TargetIssue[]) => void;
}

const TargetsModal: React.FC<TargetsModalProps> = ({ isOpen, initialTargets, onClose, onSave }) => {
  const [selectedIssues, setSelectedIssues] = useState<TargetIssue[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    setSelectedIssues(initialTargets || []);
    // Hydrate any issues missing names by fetching details
    (async () => {
      try {
        const missing = (initialTargets || []).filter(t => !t.name || !t.cname);
        if (missing.length === 0) return;
        const hydrated = await Promise.all(
          missing.map(async (t) => {
            const full = await getEntityDetails('issue', t.id);
            return { id: t.id, name: (full as any)?.name, cname: (full as any)?.cname } as TargetIssue;
          })
        );
        setSelectedIssues(prev => {
          const map = new Map<number, TargetIssue>();
          prev.forEach(p => map.set(p.id, p));
          hydrated.forEach(h => map.set(h.id, { ...map.get(h.id), ...h } as TargetIssue));
          return Array.from(map.values());
        });
      } catch {}
    })();
  }, [initialTargets]);

  const handleRemove = (issueId: number) => {
    setSelectedIssues(prev => prev.filter(i => i.id !== issueId));
  };

  const ensureIssueFromSuggestion = async (suggestion: Suggestion): Promise<Issue> => {
    // If it's an existing issue suggestion, fetch full details
    if (suggestion.type === 'issue' && (suggestion as any).id && (suggestion as any).cname) {
      const full = await getEntityDetails('issue', (suggestion as any).id as number);
      return full as Issue;
    }
    // Create issue from Wikipedia suggestion
    const createdIssue = await createIssueFromWikipedia(suggestion as any);
    return createdIssue;
  };

  const handleSelect = async (s: Suggestion) => {
    setError("");
    try {
      const full = await ensureIssueFromSuggestion(s);
      setSelectedIssues(prev => prev.some(i => i.id === full.id) ? prev : [...prev, { id: full.id, name: (full as any)?.name }]);
    } catch {
      setError("Failed to add issue. Try again.");
    }
  };

  const footer = (
    <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex sm:flex-row gap-2 px-3 py-3">
      <button
        onClick={() => onClose()}
        className="btn-secondary w-full sm:w-auto py-2 px-4"
      >
        Cancel
      </button>
      <button
        onClick={async () => { setIsSubmitting(true); onSave(selectedIssues); setIsSubmitting(false); onClose(); }}
        className={`btn-primary w-full sm:w-auto py-2 px-4 ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
        disabled={isSubmitting}
      >
        Save
      </button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Targets" footer={footer} showBackButton onBack={onClose}>
      <div className="flex flex-col gap-3 px-1 pb-3">
        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
          Select issues this action targets. You can add or remove at any time.
        </p>
        {error && (
          <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
            <Info className="mr-1 size-4" />
            <span>{error}</span>
          </div>
        )}
        {selectedIssues.length > 0 && (
          <div className="space-y-2">
            {selectedIssues.map(issue => (
              <div key={issue.id} className="flex items-center justify-between p-2 rounded-md bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover">
                <EntityAvatar entity={issue as unknown as Issue} entityType="issue" showCloseChip={false} showLink />
                <button
                  onClick={() => handleRemove(issue.id)}
                  className="p-1 rounded-full text-warning-red hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
                  aria-label="Remove issue"
                >
                  <X className="size-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        <IssueSearch
          key={`targets-search-${selectedIssues.length}`}
          onSelect={handleSelect}
          placeholder="Search for an issue to target"
          inputType="add"
          fieldRequired={false}
          excludeIssueIds={selectedIssues.map(i => i.id)}
          onEntityChipClose={() => {}}
        />
      </div>
    </Modal>
  );
};

export default TargetsModal;


